import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

//plugins
import moment from 'moment'
import { Modal, Button, Tabs, Tab, FloatingLabel, Form, Nav, Row, Col, Alert } from 'react-bootstrap';
import { TrashFill } from 'react-bootstrap-icons';
import { sortable } from 'react-sortable';

//helpers
import Api from 'helpers/Api'
import { useValidation } from 'helpers/Validation'
import { useNestedState } from 'helpers/NestedState'

//providers
import { useLanguageContext } from 'providers/Language'

//misc
import SaveButton from 'components/misc/Button'
import Info from 'components/modals/Info';
import Value from './Value';
import { TYPES } from 'constants/features';

function SortableItem(props) {
    return (
        <div {...props}>
            {props.children}
        </div>
    )
}

const SortItem = sortable(SortableItem);

function AddOrEdit(props, ref) {

    const langs = useLanguageContext();

    const defaultData = {
        langs: {},
        values: [],
        vals: []
    }

    const [state, setState] = useNestedState({
        show: false,
        id: null,
        edit: false,
        data: defaultData,
        loading: false,
        onSuccess: null,
        onEntering: null,
        onExiting: null
    });

    const [validations, setValidations] = useValidation();

    const validationModalRef = useRef(null);
    const inputFileRef = useRef(null);

    useImperativeHandle(ref, () => ({
        add: () => {
            handleShow();
        },
        edit: (id) => {
            edit(id);
        },
        hide: () => {
            hide();
        },
        onSuccess: fn => {
            setState(prev => ({
                ...prev,
                onSuccess: fn
            }));
        }
    }));

    useEffect(() => {
        loadData();
    }, [state.edit]);

    const show = (onEntering = null) => {
        setState(prev => ({
            ...prev,
            show: true,
            onEntering: onEntering,
        }));
    }

    const hide = (onExiting = null) => {
        setState(prev => ({
            ...prev,
            show: false,
            onExiting: onExiting
        }));
    }

    const handleShow = () => {
        setState(prev => ({
            ...prev,
            data: defaultData,
            id: null,
        }));

        show();
    }

    const handleClose = e => {

        if (e) {
            e.preventDefault();
        }

        setState(prev => ({
            ...prev,
            data: defaultData,
            initialData: defaultData,
            id: null,
        }));

        setValidations(null);

        hide();
    }

    const edit = id => {
        setState(prev => ({
            ...prev,
            id: id,
            edit: new Date().getTime()
        }));

        show();
    }

    const loadData = () => {
        if (!state.id) {
            return;
        }

        Api.get('features/edit?id=' + state.id)
            .then(res => {
                setState(prev => ({
                    ...prev,
                    data: res.data
                }))
            });
    }


    const handleSave = e => {
        e.preventDefault();

        setState(prev => ({
            ...prev,
            loading: true,
        }));

        setValidations(null);

        let url = state.id ? 'features/update' : 'features/add';

        let data = new FormData(e.target);

        if (state.id) {
            data.append('id', state.id)
        }

        state.data.values.map((value, i) => {
            if (value.file) {
                data.append(`values[${i}][image]`, value.file);
            }
        });

        Api.post(url, data, {
            headers: {
                'content-type': 'multipart/form-data'
            }
        }).then(res => {
            if (typeof state.onSuccess === 'function') {
                state.onSuccess(res.data)
            }

            hide()
        }).catch(error => {
            const _err = error.response;
            if (_err && _err.status && _err.status === 422) {
                hide(() => {
                    let modal = validationModalRef.current;

                    if (modal) {
                        modal.open();

                        modal.onClose(() => {
                            setValidations(_err.data.errors)
                            show();
                        });
                    }
                });
            }
        }).finally(() => {
            setState(prev => ({
                ...prev,
                loading: false,
            }));
        });
    }

    const handleInputChange = e => {
        const name = e.target.name
        const value = e.target.value

        setState(name, value, 'data')
    }

    const handleCheckboxChange = e => {
        const name = e.target.name;

        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: prev.data[name] ? false : true
            }
        }));
    }

    const handleDateChange = e => {
        const name = e.target.name + '_ymd'
        const value = e.target.value

        setState(name, value, 'data')
    }

    const addDetail = () => {
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                values: prev.data.values.concat({ langs: [] })
            }
        }));
    }

    const removeDetail = (index) => {
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                values: prev.data.values.filter((d, i) => Number(i) !== Number(index))
            }
        }));
    }

    const handleSortFiles = items => {
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                values: items
            }
        }));
    }

    const handleValidation = (langId) => {
        let cancel = false;
        if (validations && validations.langs) {
            Object.keys(validations.langs).map((element) => {
                if (Number(element) === langId) cancel = true;
            });
        }

        if (validations && validations.values) {
            Object.keys(validations.values).map((valKey) => {
                Object.keys(validations.values[valKey].langs).map((langKey) => {
                    if (Number(langKey) === langId) cancel = true;
                })
            })
        }

        return cancel;
    }

    return (
        <>
            <Info
                ref={validationModalRef}
                mainMessage="Моля, попълнете всички задължителни полета, означени с червен цвят!"
            />
            <Modal centered show={state.show} onHide={handleClose} onEntering={state.onEntering} onExiting={state.onExiting}>
                <form onSubmit={handleSave}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Характеристика
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Tabs
                            defaultActiveKey={langs.data[0].id}
                            className="mb-3"
                            transition={false}
                        >
                            {langs.data.map(lang =>
                                <Tab key={lang.id} eventKey={lang.id} title={lang.name} tabClassName={handleValidation(lang.id) ? 'validate' : ''}>
                                    <Row className="mb-3">
                                        <Col >
                                            <FloatingLabel label="Тип*">
                                                <Form.Select
                                                    name="type_id"
                                                    value={state.data.type_id || ''}
                                                    onChange={handleInputChange}
                                                    isInvalid={Boolean(validations && validations.type_id)}
                                                >
                                                    <option></option>
                                                    <option value={TYPES.COLOR}>Цвят</option>
                                                    <option value={TYPES.BRAND}>Марка</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {validations && validations.type_id && (validations.type_id[0] || validations.type_id)}
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                        </Col>
                                        <Col>
                                            <FloatingLabel label="Име">
                                                <Form.Control
                                                    type="text"
                                                    name={`langs[${lang.id}][name]`}
                                                    placeholder="Име"
                                                    onChange={(e) => handleInputChange(e)}
                                                    value={state.data.langs[lang.id]?.name || ''}
                                                    isInvalid={Boolean(validations?.langs && validations.langs[lang.id] && validations.langs[lang.id].name)}
                                                />

                                                <Form.Control.Feedback type="invalid">
                                                    {validations?.langs && validations.langs[lang.id] && validations.langs[lang.id].name && (validations.langs[lang.id].name[0] || validations.langs[lang.id].name)}
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                        </Col>
                                    </Row>
                                    <Col className="mb-3">
                                        {state.data.values.map((detail, index) =>
                                            <SortItem
                                                key={index}
                                                items={state.data.values}
                                                onSortItems={handleSortFiles}
                                                sortId={index}
                                                className="file-container"
                                            >
                                                <Value
                                                    index={index}
                                                    detail={detail}
                                                    state={state}
                                                    setState={setState}
                                                    validations={validations}
                                                    lang={lang}
                                                    handleInputChange={handleInputChange}
                                                    removeDetail={removeDetail}
                                                />
                                            </SortItem>
                                        )}
                                        <Button variant="outline-dark" size="sm" onClick={addDetail}>
                                            Добави нова стойност
                                        </Button>
                                    </Col>
                                    <Form.Check
                                        type="switch"
                                        name="is_public"
                                        label="Публична"
                                        className="mb-3 mt-3"
                                        checked={Number(state.data?.is_public) === 1}
                                        onChange={handleCheckboxChange}
                                    />
                                </Tab>
                            )}
                        </Tabs>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Отказ
                        </Button>
                        <SaveButton
                            loading={state.loading}
                            className="save"
                        />
                    </Modal.Footer>
                </form >
            </Modal >
        </>
    )
}

export default forwardRef(AddOrEdit);
